import {
  DEFAULT_GIT_PROJECT_SETTINGS,
  type GitBranchSubject,
  type GitChangeRequestState,
  type GitCiStatus,
  type GitHostKind,
  type GitLinkSubjectKind,
  type GitProjectSettings,
  type GitReviewState,
  buildBranchName,
  slugifyBranchPart,
} from '@apm/contracts';
import { displayDateTime } from '../../lib/date';

/**
 * Hàm thuần của tab Git và khối "Phát triển" — APM-GIT-e,
 * docs/plan-git-integration.md §8.1–§8.3. Không React, không mạng: thứ ở đây có
 * unit test riêng (`git-format.spec.ts`) và các màn chỉ việc gọi.
 */

// ── Mốc thời gian ───────────────────────────────────────────────────────────

/**
 * `asOf` của URL → dạng API nhận. Các endpoint đọc của tab khai
 * `z.iso.datetime()` — **bắt buộc UTC `Z`** — trong khi `?asOf=` của trang dự án
 * là một chuỗi tự do dùng chung với Báo cáo / Ngân sách / Kiểm thử (ngày thuần
 * `2026-09-01`, hoặc ISO có offset). Chuỗi không đọc được ⇒ `undefined` (đọc
 * "bây giờ") thay vì một lỗi 400 cho cả tab.
 */
export function normalizeGitAsOf(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;
  const at = new Date(raw);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}

/**
 * Phần **ngày** (`dd/MM/yyyy`) của một mốc, theo đồng hồ người xem. Không dùng
 * `displayDate`: hàm ấy đọc ngày **UTC**, đúng cho cột `DATE` nhưng lệch một ngày
 * với một mốc trước 07:00 sáng ở +07:00 (bẫy APM-TZ).
 */
export function displayMomentDate(value: string | null | undefined): string {
  const full = displayDateTime(value);
  return full === '—' ? full : full.slice(0, 10);
}

export type GitAgeUnit = 'minutes' | 'hours' | 'days';

/**
 * Khoảng thời gian giữa hai mốc, ở đơn vị lớn nhất còn ≥ 1 — "tuổi" của một MR
 * (`3 ngày`), "kéo 5 phút trước". Mốc lệch thứ tự ⇒ 0 phút.
 */
export function gitAge(from: string, to: Date | string): { unit: GitAgeUnit; value: number } {
  const ms = Math.max(0, new Date(to).getTime() - new Date(from).getTime());
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return { unit: 'minutes', value: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { unit: 'hours', value: hours };
  return { unit: 'days', value: Math.floor(hours / 24) };
}

/** Một khoảng (ms) của `cycleBreakdown` ở cùng ba đơn vị; `null` = pha thiếu mốc. */
export function gitDuration(ms: number | null): { unit: GitAgeUnit; value: number } | null {
  if (ms === null) return null;
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return { unit: 'minutes', value: minutes };
  const hours = Math.round(minutes / 60);
  if (hours < 24) return { unit: 'hours', value: hours };
  return { unit: 'days', value: Math.round(hours / 24) };
}

// ── Tên nhánh (§8.3) ────────────────────────────────────────────────────────

/** `GOLD-123` → `GOLD` · `GOLD-I12` → `GOLD`. Khoá dự án không chứa `-`. */
export function projectKeyOfCode(code: string): string {
  const dash = code.lastIndexOf('-');
  return dash > 0 ? code.slice(0, dash) : code;
}

/**
 * Slug của loại việc từ **tên** của nó. `TaskView` chỉ mang tên loại; slug ở
 * server (`slugifyTaskType`) là cùng phép bỏ dấu này, nên "Lỗi" ⇒ `loi` khớp
 * đúng dòng của bảng `{type}`.
 */
function typeSlugOf(name: string | null | undefined): string | null {
  return name ? slugifyBranchPart(name, 60) || null : null;
}

/** Task → thứ `buildBranchName` cần. */
export function branchSubjectOfTask(task: {
  readonly key: string;
  readonly number: number;
  readonly title: string;
  readonly taskTypeName?: string | null | undefined;
}): GitBranchSubject {
  return {
    projectKey: projectKeyOfCode(task.key),
    number: task.number,
    title: task.title,
    kind: 'task',
    typeSlug: typeSlugOf(task.taskTypeName),
  };
}

/** Issue vận hành → thứ `buildBranchName` cần (ra `…-i12-…`). */
export function branchSubjectOfIssue(issue: {
  readonly code: string;
  readonly number: number;
  readonly title: string;
  readonly type?: { readonly name: string } | null | undefined;
}): GitBranchSubject {
  return {
    projectKey: projectKeyOfCode(issue.code),
    number: issue.number,
    title: issue.title,
    kind: 'issue',
    typeSlug: typeSlugOf(issue.type?.name),
  };
}

/**
 * Tên nhánh gợi ý cho một việc. Cấu hình chưa tải / dự án chưa lưu gì ⇒ định dạng
 * mặc định: nút "Sao chép tên nhánh" **luôn** ra một tên dùng được (§8.3 — đường
 * làm tay của cả tính năng, không cần token, không cần kho).
 */
export function suggestedBranchName(
  subject: GitBranchSubject,
  settings: GitProjectSettings | null | undefined,
): string {
  const effective = settings ?? DEFAULT_GIT_PROJECT_SETTINGS;
  return buildBranchName(effective.branchFormat, subject, effective.branchTypeMap);
}

// ── Gắn link tay (§8.2) ─────────────────────────────────────────────────────

/**
 * Thứ người dùng gõ / dán vào ô "gắn link tay" → `subjectRef` API nhận, hoặc
 * `null` khi không đọc ra.
 *
 * - MR / PR: `45`, `!45`, `#45`, hoặc link `…/merge_requests/45`, `…/pull/45`.
 * - Commit: sha hex 7–40 ký tự, hoặc link `…/commit/<sha>` (API tra đúng sha
 *   **đầy đủ**; sha rút gọn sẽ nhận câu "chưa có trong dữ liệu đã kéo về").
 * - Nhánh: tên nhánh, bỏ tiền tố `refs/heads/` nếu có.
 */
export function parseLinkSubject(kind: GitLinkSubjectKind, raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  if (kind === 'change_request') {
    const fromUrl = /\/(?:merge_requests|pull)\/(\d{1,9})(?:[/?#].*)?$/.exec(text);
    const plain = /^[!#]?(\d{1,9})$/.exec(text);
    const digits = fromUrl?.[1] ?? plain?.[1];
    return digits && Number(digits) > 0 ? String(Number(digits)) : null;
  }
  if (kind === 'commit') {
    const fromUrl = /\/commits?\/([0-9a-f]{7,40})(?:[/?#].*)?$/i.exec(text);
    const sha = fromUrl?.[1] ?? text;
    return /^[0-9a-f]{7,40}$/i.test(sha) ? sha.toLowerCase() : null;
  }
  const name = text.replace(/^refs\/heads\//, '');
  return name.length > 0 && name.length <= 255 && !/\s/.test(name) ? name : null;
}

// ── Nhãn và màu ─────────────────────────────────────────────────────────────

/**
 * Địa chỉ do máy chủ git trả về (`webUrl`, `ciUrl`) chỉ thành link bấm được khi
 * là `http(s)` — văn bản từ git là **không tin cậy** (§11), và một `javascript:`
 * trong `href` chạy trên origin của app.
 */
export function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** GitLab viết `!45`, GitHub viết `#45`. */
export function changeRequestRef(hostKind: GitHostKind | undefined, number: number): string {
  return `${hostKind === 'github' ? '#' : '!'}${number}`;
}

/**
 * `!45` / `#45` suy từ **địa chỉ** của MR, cho nơi không có sẵn loại máy chủ (khối
 * "Phát triển" chỉ nhận `GitChangeRequestView`): PR của GitHub nằm dưới `/pull/`.
 */
export function changeRequestRefOfUrl(webUrl: string, number: number): string {
  return changeRequestRef(/\/pull\/\d+/.test(webUrl) ? 'github' : 'gitlab', number);
}

export function shortSha(sha: string): string {
  return sha.slice(0, 8);
}

/** Khoá của một MR ở URL (`?gitCr=`): `<repositoryId>:<number>`. */
export function changeRequestKey(repositoryId: string, number: number): string {
  return `${repositoryId}:${number}`;
}

/** Đọc lại `changeRequestKey`. Chuỗi lạ (link cũ, gõ tay) ⇒ `null`, không mở ngăn nào. */
export function parseChangeRequestKey(
  key: string | undefined | null,
): { repositoryId: string; number: number } | null {
  const match = /^([0-9a-f-]{36}):([1-9]\d{0,8})$/i.exec(key ?? '');
  return match ? { repositoryId: match[1]!, number: Number(match[2]) } : null;
}

/** Trạng thái hiển thị của một MR: nháp là một trạng thái riêng của MR **đang mở**. */
export type GitChangeRequestBadge = GitChangeRequestState | 'draft';

export function changeRequestBadge(changeRequest: {
  readonly state: GitChangeRequestState;
  readonly isDraft: boolean;
}): GitChangeRequestBadge {
  return changeRequest.state === 'open' && changeRequest.isDraft ? 'draft' : changeRequest.state;
}

/*
  Bảng lớp viết sẵn — Tailwind quét **mã nguồn**, nên một tên lớp ghép lúc chạy
  (`bg-rag-` + màu) không tồn tại trong bản build và nhãn hiện ra trong suốt
  (bẫy APM-1303).
*/
export const CHANGE_REQUEST_BADGE_CLASS: Readonly<Record<GitChangeRequestBadge, string>> = {
  open: 'bg-rag-green/15 text-rag-green',
  draft: 'bg-muted text-muted-foreground',
  merged: 'bg-primary/15 text-primary',
  closed: 'bg-rag-red/15 text-rag-red',
};

/** Vạch mép trái của thẻ MR trên điện thoại. */
export const CHANGE_REQUEST_STRIPE_CLASS: Readonly<Record<GitChangeRequestBadge, string>> = {
  open: 'border-l-rag-green',
  draft: 'border-l-border-strong',
  merged: 'border-l-primary',
  closed: 'border-l-rag-red',
};

export const REVIEW_STATE_CLASS: Readonly<Record<GitReviewState, string>> = {
  none: 'text-muted-foreground',
  requested: 'text-rag-amber',
  changes_requested: 'text-rag-red',
  approved: 'text-rag-green',
};

/** Màu chấm CI. `none` không vẽ chấm nào — xem `GitCiDot`. */
export const CI_DOT_CLASS: Readonly<Record<GitCiStatus, string>> = {
  none: 'bg-transparent',
  pending: 'bg-rag-amber',
  running: 'bg-primary animate-pulse',
  success: 'bg-rag-green',
  failed: 'bg-rag-red',
  canceled: 'bg-border-strong',
};
